import React from "react";
import { useFormik } from "formik";

import CustomInput from "../formik/CustomInput.tsx";
import CustomTextArea from "../formik/CustomTextArea.tsx";
import CustomSelectField from "../formik/CustomSelectField.tsx";

import { INewDeduction } from "../../interfaces/others/INewDeduction.ts";

import {
  FrequencyList,
  DeductionInitial,
} from "../../interfaces/formik/IDeduction.ts";
import { DeductionSchema } from "../../validation/schemas/DeductionSchema.ts";
import { notification } from "../../utils/Notifications.tsx";
import Loader from "../Loader.tsx";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import {
  saveDeduction,
  findDeductionsByPartner,
  updateDeduction,
  DeductionTemplate,
} from "../../services/deductions.service.ts";
import { setSelectedPartners } from "../../store/slices/payrollSlice.ts";
import TemplateAutocomplete from "./TemplateAutocomplete.tsx";

const NewDeduction: React.FC<INewDeduction> = ({
  close,
  onRefresh,
  deductionToEdit,
  isEditing = false,
}) => {
  const dispatch = useDispatch();
  const { selectedPartners } = useSelector((state: RootState) => state.payroll);
  const [loading, setLoading] = React.useState(false);

  const formik = useFormik({
    initialValues:
      isEditing && deductionToEdit
        ? {
            name: deductionToEdit.name || "",
            description: deductionToEdit.description || "",
            amount: deductionToEdit.amount || 0,
            frequency: (() => {
              const found = FrequencyList.find(
                (item) =>
                  item.name.toLowerCase() ===
                    deductionToEdit.frequency?.toLowerCase() ||
                  item.name.toLowerCase() ===
                    deductionToEdit.frequency
                      ?.toLowerCase()
                      ?.replace("único", "unico")
              );
              return found?.id || -99;
            })(),
            startDate: deductionToEdit.startDate || "",
            endDate: deductionToEdit.endDate || "",
            applicationDate: deductionToEdit.applicationDate || "",
            status: deductionToEdit.status || "activo",
          }
        : DeductionInitial,
    validationSchema: DeductionSchema,
    onSubmit: async (values) => {
      setLoading(true);

      try {
        if (isEditing) {
          if (!deductionToEdit) {
            notification(
              "Error ⚠️",
              "No se encontró la deducción a editar",
              "danger"
            );
            return;
          }

          const frequency = FrequencyList.find(
            (item) => item.id === values.frequency
          )?.name;

          const { applicationDate, startDate, endDate, ...data } = values;

          const deductionData = {
            name: data.name,
            description: data.description,
            amount: Number(data.amount),
            frequency: frequency?.toLowerCase(),
            ...(applicationDate !== "" && { applicationDate }),
            ...(startDate !== "" && { startDate }),
            ...(endDate !== "" && values.frequency !== 4 && { endDate }),
          };

          const response = await updateDeduction(
            deductionToEdit.id,
            deductionData
          );

          notification("", "Deducción actualizada exitosamente", "success");
        } else {
          if (!selectedPartners || selectedPartners.length === 0) {
            notification(
              "Error ⚠️",
              "No hay colaboradores seleccionados",
              "danger"
            );
            return;
          }

          if (
            !values.name ||
            !values.description ||
            !values.amount ||
            values.frequency === -99
          ) {
            notification(
              "Error ⚠️",
              "Complete todos los campos requeridos",
              "danger"
            );
            return;
          }

          const frequency = FrequencyList.find(
            (item) => item.id === values.frequency
          )?.name;


          const { applicationDate, startDate, endDate, ...data } = values;

         
          let createdCount = 0;
          const processedPartnerIds = new Set<number>();

          for (const partnerId of selectedPartners) {

            const deductionData = {
              name: data.name,
              description: data.description,
              amount: Number(data.amount),
              frequency: frequency?.toLowerCase(),
              partnerId: partnerId,
              ...(applicationDate !== "" && { applicationDate }),
              ...(startDate !== "" && { startDate }),
              ...(endDate !== "" && values.frequency !== 4 && { endDate }),
            };


            try {
              const response = await saveDeduction(deductionData);
              createdCount++;

              processedPartnerIds.add(partnerId);
            } catch (error) {
              console.error(`Error processing partner ${partnerId}:`, error);
              throw error;
            }
          }

          let message = "";
          if (createdCount > 0) {
            message = `${createdCount} deducciones creadas exitosamente`;
          } else {
            message = "Operación completada";
          }

          notification("", message, "success");
          dispatch(setSelectedPartners([]));
        }

        close();
        onRefresh?.();
      } catch (error) {
        notification("Error ⚠️", "Error al guardar las deducciones", "danger");
      } finally {
        setLoading(false);
      }
    },
  });

  React.useEffect(() => {
    if (formik.values.frequency === 4) {
      formik.setFieldValue('startDate', '');
      formik.setFieldValue('endDate', '');
    } else {
      formik.setFieldValue('applicationDate', '');
    }
  }, [formik.values.frequency]);

  const handleTemplateSelect = (template: DeductionTemplate) => {
    formik.setFieldValue("name", template.name);
    formik.setFieldValue("description", template.description);
    formik.setFieldValue("amount", template.amount);
    notification("", `Plantilla "${template.name}" aplicada`, "success");
  };

  return (
    <React.Fragment>
      <Loader isLoading={loading} />
      
      <div>
        <CustomInput
          formik={formik}
          field="name"
          label="Nombre"
          placeholder="Nombre de la deducción"
          required
          sm={8}
        />
        
        {!isEditing && (
          <TemplateAutocomplete
            searchValue={formik.values.name}
            onTemplateSelect={handleTemplateSelect}
          />
        )}
      </div>
      <CustomTextArea
        field="description"
        formik={formik}
        label="Descripción"
        placeholder="Escribe alguna descripción"
        smLabel={3}
        sm={8}
        required
      />

      <div className="mt-3" />
      <CustomInput
        formik={formik}
        field="amount"
        label="Monto"
        type="number"
        placeholder="$100.00"
        required
        sm={8}
      />

      <CustomSelectField
        formik={formik}
        field="frequency"
        list={FrequencyList}
        label="Frecuencia"
        smLabel={3}
        sm={8}
        required
      />

      <div className="mt-3" />
          {formik.values.frequency !== 4 && ( // 4 = "único" en FrequencyList
            <CustomInput
              formik={formik}
              field="startDate"
              label="Fecha de inicio"
              type="date"
              required
              sm={8}
            />
          )}

      {formik.values.frequency === 4 && ( // 4 = "único" en FrequencyList
        <div className="mt-3">
          <CustomInput
            formik={formik}
            field="applicationDate"
            label="Fecha de aplicación"
            type="date"
            required
            sm={8}
          />
        </div>
      )}

      <div className="mt-3" />
          {formik.values.frequency !== 4 && ( // 4 = "único" en FrequencyList
            <CustomInput
              formik={formik}
              field="endDate"
              label="Fecha de fin"
              type="date"
              required
              sm={8}
            />
          )}

      {/* Fechas ocultas temporalmente */}

      <div className="d-flex justify-content-end">
        <button className="btn btn-secondary me-2" onClick={() => close()}>
          Cancelar
        </button>
        <button
          className="btn btn-success"
          onClick={() => formik.handleSubmit()}
        >
          {isEditing ? "Actualizar" : "Guardar"}
        </button>
      </div>
    </React.Fragment>
  );
};

export default NewDeduction;
